import React, { useState } from 'react';
import { Send, ShieldCheck, Plus, Trash2 } from 'lucide-react';
import type { FormDefinition, FormField } from '../types';

export interface FormFunnelBuilderProps {
  initialForm?: FormDefinition;
  onSave: (form: FormDefinition) => void;
}

// FormFunnelBuilder visualizes conversion funnel fields, anti-spam, and host target action routing.
export function FormFunnelBuilder({ initialForm, onSave }: FormFunnelBuilderProps): React.JSX.Element {
  const [form, setForm] = useState<FormDefinition>(
    initialForm || {
      id: '',
      name: 'Admissions Enquiry Form',
      targetHostAction: 'admissions.enquiry',
      fields: [
        { name: 'full_name', label: 'Full Name', type: 'text', required: true },
        { name: 'email', label: 'Email Address', type: 'email', required: true },
        { name: 'phone', label: 'Phone Number', type: 'phone', required: false },
      ],
      spamProtection: 'turnstile',
    }
  );

  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldLabel, setNewFieldLabel] = useState('');

  const addField = () => {
    if (!newFieldName.trim() || !newFieldLabel.trim()) return;
    const newField: FormField = { name: newFieldName.trim(), label: newFieldLabel.trim(), type: 'text', required: false };
    const updated = { ...form, fields: [...form.fields, newField] };
    setForm(updated);
    setNewFieldName('');
    setNewFieldLabel('');
    onSave(updated);
  };

  const removeField = (index: number) => {
    const updated = { ...form, fields: form.fields.filter((_, i) => i !== index) };
    setForm(updated);
    onSave(updated);
  };

  return (
    <div className="p-6 bg-card border border-border rounded-xl flex flex-col gap-6">
      <div className="flex items-center justify-between border-b border-border pb-4">
        <div className="flex items-center gap-2">
          <Send className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold text-foreground">Conversion Form & Funnel Builder</h2>
        </div>
        <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground bg-muted/40 px-3 py-1.5 rounded-lg border border-border">
          <ShieldCheck className="h-4 w-4 text-accent" />
          <span>Cloudflare Turnstile Active</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-bold text-muted-foreground uppercase tracking-wider block mb-1">
            Form Name
          </label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full text-base md:text-sm p-2 rounded border border-border bg-background text-foreground"
          />
        </div>
        <div>
          <label className="text-xs font-bold text-muted-foreground uppercase tracking-wider block mb-1">
            Target Host Action
          </label>
          <input
            type="text"
            value={form.targetHostAction}
            onChange={(e) => setForm({ ...form, targetHostAction: e.target.value })}
            className="w-full text-base md:text-sm p-2 rounded border border-border bg-background text-foreground font-mono"
            placeholder="e.g. admissions.enquiry, crm.lead"
          />
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Form Fields</span>
        {form.fields.map((f, i) => (
          <div key={`${f.name}-${i}`} className="flex items-center justify-between p-3 rounded-lg border border-border bg-background">
            <div className="flex items-center gap-3">
              <span className="font-medium text-sm text-foreground">{f.label}</span>
              <span className="text-xs font-mono text-muted-foreground">({f.name})</span>
              {f.required && <span className="text-[10px] font-bold text-primary uppercase">Required</span>}
            </div>
            <button
              type="button"
              onClick={() => removeField(i)}
              className="p-1 text-muted-foreground hover:text-primary min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label="Remove field"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>

      <div className="flex flex-col md:flex-row gap-3 pt-2 border-t border-border">
        <input
          type="text"
          value={newFieldLabel}
          onChange={(e) => setNewFieldLabel(e.target.value)}
          placeholder="Field Label (e.g. Desired Major)"
          className="flex-1 text-base md:text-sm p-2 rounded border border-border bg-background text-foreground"
        />
        <input
          type="text"
          value={newFieldName}
          onChange={(e) => setNewFieldName(e.target.value)}
          placeholder="Key (e.g. desired_major)"
          className="flex-1 text-base md:text-sm p-2 rounded border border-border bg-background text-foreground font-mono"
        />
        <button
          type="button"
          onClick={addField}
          className="flex items-center justify-center gap-1.5 px-4 py-2 rounded bg-primary text-primary-foreground font-medium text-sm hover:opacity-90 min-h-[44px]"
        >
          <Plus className="h-4 w-4" />
          <span>Add Field</span>
        </button>
      </div>
    </div>
  );
}
